import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { computed, makeObservable, reaction } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import CheckControl from 'modules/core/components/Inputs/CheckControl'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import leaves from 'modules/timeoff/store/collections/leaves'

import Item from './Item'
import type { RelapseOption } from './Item'

interface Props {
  employee: Employee | null | undefined
  form: LeaveForm
  year: number
  readOnly: boolean
}

class Relapse extends React.Component<Props> {
  handler: (() => void) | null | undefined
  handlerRelapseLeave: (() => void) | null | undefined

  constructor(props: Props) {
    super(props)
    makeObservable(this, {
      leaves: computed,
      options: computed,
    })

    const { form } = props
    this.handler = reaction(
      () => form.get('has_previous_relapse').value,
      (hasPreviousRelapse) => {
        if (hasPreviousRelapse) return

        form.setValues({
          relapse_on: null,
          relapse_leave_id: null,
        })
      }
    )
    this.handlerRelapseLeave = reaction(
      () => +form.get('relapse_leave_id').value,
      (relapseLeaveId) => {
        if (!relapseLeaveId) return

        const leave = leaves.find({
          id: relapseLeaveId,
        })

        if (!leave) {
          throw Error(`Error finding leaves with id = ${relapseLeaveId}`)
        }

        form.setValues({
          relapse_on: leave.attributes.start_on,
        })
      }
    )
  }

  componentWillUnmount() {
    if (this.handler) {
      this.handler()
      this.handler = null
    }

    if (this.handlerRelapseLeave) {
      this.handlerRelapseLeave()
      this.handlerRelapseLeave = null
    }
  }

  get leaves() {
    const { employee } = this.props

    if (!employee) return []

    return leaves.models.filter((l) => {
      const finishOn = moment(l.attributes.finish_on)
      const today = moment()

      return (
        l.leaveType?.isMedicalLeave &&
        employee.attributes.id === l.attributes.employee_id &&
        finishOn.isBefore(today)
      )
    })
  }

  get options(): RelapseOption[] {
    const { year, form } = this.props

    return this.leaves.map((leave) => ({
      field: form.get('relapse_leave_id'),
      name: 'relapse_leave',
      leave: leave,
      year: year,
      value: leave.attributes.id,
    }))
  }

  renderRelapseSelector() {
    const { form, readOnly } = this.props

    if (!form.get('has_previous_relapse').value) return null

    const readOnlyRelapseOn = readOnly || !!form.get('relapse_leave_id').value

    return (
      <Box paddingTop="s24">
        {this.options.length > 0 && (
          <>
            <BoxList>
              {this.options.map((relapse) => (
                <Item key={relapse.leave.attributes.id} {...relapse} />
              ))}
            </BoxList>
            <Text.Normal color="grey800">
              {i18n.t('time_off.medical_leave.relapse.description')}
            </Text.Normal>
          </>
        )}
        <DayPicker
          field={form.get('relapse_on')}
          label={i18n.t('time_off.medical_leave.relapse.label')}
          readonly={readOnlyRelapseOn}
        />
      </Box>
    )
  }

  render() {
    const { form, readOnly } = this.props

    return (
      <>
        <CheckControl
          field={form.get('has_previous_relapse')}
          behaviour="checkbox"
          readonly={readOnly}
          skin="checkbox"
        >
          {() => i18n.t('time_off.medical_leave.relapse.has_previous_relapse')}
        </CheckControl>
        {this.renderRelapseSelector()}
      </>
    )
  }
}

export default observer(Relapse)
